import Localbase from "localbase";
import QuestionData, { version } from "../450DSAFinal";
import { slugify } from "../utils/slug";
import { mergeTopicData, normalizeQuestion, recomputeTopic } from "../utils/merge";

let db = new Localbase("db");
window.db = db;
db.config.debug = false;

const COLLECTION = "450dsaArchive";
const APPLY_COLLECTION = "applyArchive";
const APPLY_DOC = "state";
const localVersion = localStorage.getItem("450version");
window.localVersion = localVersion;
window.version = version;

// Seed the DB from the bundled list (first run only). Writes are awaited
// sequentially so the first read always sees a fully-seeded collection
// (the original fire-and-forget loop could race the read on slower /
// IndexedDB-polyfilled environments).
export function insertData(callback) {
	(async () => {
		for (const topic of QuestionData) {
			await db.collection(COLLECTION).add(topic, slugify(topic.topicName));
		}
		getData(callback);
	})();
}

// Wipe + rewrite the whole collection. Used on version bumps and imports so
// the persisted data always matches the merged (normalized) shape. Writes are
// sequential to avoid overlapping IndexedDB transactions.
function persistAll(topics, callback) {
	(async () => {
		try {
			await db.collection(COLLECTION).delete();
		} catch (e) {
			/* nothing to delete yet */
		}
		for (const t of topics) {
			await db.collection(COLLECTION).add(t, slugify(t.topicName));
		}
		callback && callback(topics);
	})();
}

// Read everything, merge non-destructively with the seed, and hand back a
// fully normalized, generically-keyed list. Custom topics/problems and all
// user state survive seed (version) updates.
export function getData(callback) {
	db.collection(COLLECTION)
		.get()
		.then((data) => {
			if (!data || data.length === 0) {
				insertData(callback);
				return;
			}
			data.sort((a, b) => a.position - b.position);
			const merged = mergeTopicData(QuestionData, data);

			const needsRewrite = localVersion === null || localVersion === undefined || parseInt(localVersion) !== version;
			if (needsRewrite) {
				localStorage.setItem("450version", version);
				persistAll(merged, () => callback(merged));
			} else {
				callback(merged);
			}
		});
}

export function getTopicData(key, callback) {
	db.collection(COLLECTION)
		.doc(key)
		.get()
		.then((document) => {
			callback(document);
		});
}

export function updateDBData(key, updateData) {
	db.collection(COLLECTION).doc(key).update(updateData);
}

// ---- Custom problem / topic management (keeps the list generic) ----

// Append a user-defined problem to an existing topic.
export function addProblemToTopic(slug, problem, callback) {
	db.collection(COLLECTION)
		.doc(slug)
		.get()
		.then((topic) => {
			if (!topic) {
				getData(callback);
				return;
			}
			const questions = (topic.questions || []).slice();
			questions.push(normalizeQuestion({ ...problem, Custom: true }, topic.topicName));
			db.collection(COLLECTION)
				.doc(slug)
				.update({ questions, started: topic.started })
				.then(() => getData(callback));
		});
}

// Create a brand new (custom) topic. Resolves with { ok, reason, slug }.
export function addTopic(topicName, callback) {
	const slug = slugify(topicName);
	if (!slug) {
		callback && callback({ ok: false, reason: "empty" });
		return;
	}
	db.collection(COLLECTION)
		.get()
		.then((data) => {
			if (data.some((t) => slugify(t.topicName) === slug)) {
				callback && callback({ ok: false, reason: "exists" });
				return;
			}
			const topic = recomputeTopic({
				topicName: topicName.trim(),
				position: data.length,
				started: false,
				questions: [],
				Custom: true,
			});
			db.collection(COLLECTION)
				.add(topic, slug)
				.then(() => callback && callback({ ok: true, slug }));
		});
}

// Remove a single (custom) problem from a topic by its index.
export function deleteProblem(slug, index, callback) {
	db.collection(COLLECTION)
		.doc(slug)
		.get()
		.then((topic) => {
			if (!topic) {
				getData(callback);
				return;
			}
			const questions = (topic.questions || []).slice();
			questions.splice(index, 1);
			const recomputed = recomputeTopic({ ...topic, questions });
			db.collection(COLLECTION)
				.doc(slug)
				.update({
					questions: recomputed.questions,
					doneQuestions: recomputed.doneQuestions,
					started: recomputed.started,
				})
				.then(() => getData(callback));
		});
}

// Delete an entire custom topic.
export function deleteTopic(slug, callback) {
	db.collection(COLLECTION)
		.doc(slug)
		.delete()
		.then(() => getData(callback));
}

export function resetDBData(callback) {
	db.collection(COLLECTION)
		.delete()
		.then((response) => {
			localStorage.removeItem("450version");
			db.collection(APPLY_COLLECTION)
				.delete()
				.catch(() => {})
				.then(() => callback(response));
		})
		.catch((error) => {
			console.log("There was an error, do something else", error);
		});
}

export function exportDBData(callback) {
	db.collection(COLLECTION)
		.get()
		.then((data) => {
			data.sort((a, b) => a.position - b.position);
			callback(data);
		});
}

export function importDBData(data, callback) {
	const merged = mergeTopicData(QuestionData, data || []);
	persistAll(merged, () => {
		localStorage.setItem("450version", version);
		callback(merged);
	});
}

// ---- Apply (jobs) tracking: a single doc holding the user's per-company state ----
// Kept in its own collection so it travels independently of the 450 progress and
// never collides with the topic docs.

const emptyApply = () => ({ blocklist: [], tracked: {}, custom: [] });

export function getApplyData(callback) {
	db.collection(APPLY_COLLECTION)
		.doc(APPLY_DOC)
		.get()
		.then((doc) => {
			if (!doc) {
				callback(emptyApply());
				return;
			}
			callback({
				blocklist: Array.isArray(doc.blocklist) ? doc.blocklist : [],
				tracked: doc.tracked && typeof doc.tracked === "object" ? doc.tracked : {},
				custom: Array.isArray(doc.custom) ? doc.custom : [],
			});
		})
		.catch(() => callback(emptyApply()));
}

export function updateApplyDataDB(applyData) {
	const payload = {
		blocklist: (applyData && applyData.blocklist) || [],
		tracked: (applyData && applyData.tracked) || {},
		custom: (applyData && applyData.custom) || [],
	};
	db.collection(APPLY_COLLECTION)
		.doc(APPLY_DOC)
		.get()
		.then((doc) => {
			if (doc) {
				db.collection(APPLY_COLLECTION).doc(APPLY_DOC).update(payload);
			} else {
				db.collection(APPLY_COLLECTION).add(payload, APPLY_DOC);
			}
		})
		.catch(() => {
			db.collection(APPLY_COLLECTION).add(payload, APPLY_DOC);
		});
}
